import React, { useMemo } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap-v5';
import clsx from 'clsx';
import appMoment from 'src/helpers/app-moment';

interface IProps {
  value: string | moment.Moment | Date;
  formatStr?: string;
  todayFormatStr?: string;
  relative?: boolean;
  addSuffix?: boolean;
  fallback?: string;
  tooltip?: boolean;
  tooltipFormat?: string;
  as?: React.ElementType;
  className?: string;
}

/**
 * @param {string | number | Date} value - Gösterilecek tarih verisi.
 * @param {string} formatStr - 'dd.MM.yyyy', 'HH:mm' gibi format stringi.
 * @param {string} todayFormatStr - Bugün için format stringi.
 * @param {boolean} relative - True ise "3 dakika önce" gibi gösterir.
 * @param {boolean} addSuffix - Relative modda "önce/sonra" ekini kontrol eder.
 * @param {string} fallback - Tarih geçersizse ne yazsın? (Default: "-")
 * @param {boolean} tooltip - Tooltip gösterilsin mi? (Default: true)
 * @param {string} tooltipFormat - Hover yapınca görünecek tam tarih formatı.
 * @param {React.ElementType} as - Render edilecek HTML etiketi (span, div, p...).
 */
const DateTimeText: React.FC<IProps> = ({
  value,
  formatStr = 'DD.MM.YYYY, HH:mm',
  todayFormatStr = formatStr,
  relative = false,
  addSuffix = true,
  fallback = '-',
  tooltip = true,
  tooltipFormat = 'D MMMM YYYY, dddd - HH:mm:ss',
  as: Component = 'time',
  className = '',
  ...props
}) => {
  const tooltipId = Math.random().toString(36).substring(2, 15);

  const dateObj = useMemo(() => {
    if (!value) return null;
    if (appMoment.isMoment(value)) return value;
    const d = appMoment(value);
    return d.isValid() ? d : null;
  }, [value]);

  const displayText = useMemo<string>(() => {
    let text = '';
    if (!dateObj) return '';

    const isDateToday = dateObj.isSame(appMoment(), 'day');

    if (relative) {
      text = dateObj.fromNow(!addSuffix);
    } else if (isDateToday && todayFormatStr) {
      text = dateObj.format(todayFormatStr);
    } else {
      text = dateObj.format(formatStr);
    }

    return text;
  }, [dateObj, formatStr, relative, addSuffix, todayFormatStr]);

  if (!dateObj) {
    return <span className={clsx('w-fit', className)}>{fallback}</span>;
  }

  const fullDateTitle = dateObj.format(tooltipFormat);
  const isoDate = dateObj.toISOString();

  const Content = (
    <Component dateTime={isoDate} className={clsx('w-fit _dtt', className)} {...props}>
      {displayText}
    </Component>
  );

  if (!tooltip) return Content;
  return (
    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip id={`dt-${tooltipId}`}>
          <span>{fullDateTitle}</span>
        </Tooltip>
      }
    >
      {Content}
    </OverlayTrigger>
  );
};

export default DateTimeText;
